import React from 'react';

import { Text } from '@react-pdf/renderer';

import { TargetFunction, TargetType } from '../store/reducers/workspace.reducer.ts';

/**
 * Returns the correct mathematical symbol for photometric measurements
 * based on the function type and target type. Works with react-pdf.
 */
export function getPhotometricSymbolReactPdf(func: TargetFunction, type: TargetType): React.ReactElement | string {
  // Handle uniformity ratios - special formatting
  if (func === 'Uo') {
    switch (type) {
      case 'AVERAGE':
        return (
          <>
            Ū<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>0</Text>
          </>
        );
      case 'MIN':
        return (
          <>
            U<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>0,min</Text>
          </>
        );
      case 'MAX':
        return (
          <>
            U<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>0,max</Text>
          </>
        );
      default:
        return (
          <>
            U<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>0</Text>
          </>
        );
    }
  }
  if (func === 'Ul') {
    switch (type) {
      case 'AVERAGE':
        return (
          <>
            Ū<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>l</Text>
          </>
        );
      case 'MIN':
        return (
          <>
            U<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>l,min</Text>
          </>
        );
      case 'MAX':
        return (
          <>
            U<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>l,max</Text>
          </>
        );
      default:
        return (
          <>
            U<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>l</Text>
          </>
        );
    }
  }
  if (func === 'Uow') {
    switch (type) {
      case 'AVERAGE':
        return (
          <>
            Ū<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>0</Text>w
          </>
        );
      case 'MIN':
        return (
          <>
            U<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>0</Text>w
            <Text style={{ verticalAlign: 'sub', fontSize: 8 }}>,min</Text>
          </>
        );
      case 'MAX':
        return (
          <>
            U<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>0</Text>w
            <Text style={{ verticalAlign: 'sub', fontSize: 8 }}>,max</Text>
          </>
        );
      default:
        return (
          <>
            U<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>0</Text>w
          </>
        );
    }
  }

  // Handle REI (Edge Illuminance Ratio)
  if (func === 'REI') {
    switch (type) {
      case 'AVERAGE':
        return (
          <>
            R̄<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>EI</Text>
          </>
        );
      case 'MIN':
        return (
          <>
            R<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>EI,min</Text>
          </>
        );
      case 'MAX':
        return (
          <>
            R<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>EI,max</Text>
          </>
        );
      default:
        return (
          <>
            R<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>EI</Text>
          </>
        );
    }
  }

  // Handle fTI (Threshold Increment)
  if (func === 'fTI') {
    switch (type) {
      case 'AVERAGE':
        return (
          <>
            f̄<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>TI</Text>
          </>
        );
      case 'MIN':
        return (
          <>
            f<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>TI,min</Text>
          </>
        );
      case 'MAX':
        return (
          <>
            f<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>TI,max</Text>
          </>
        );
      default:
        return (
          <>
            f<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>TI</Text>
          </>
        );
    }
  }

  // Handle Ev (vertical illuminance)
  if (func === 'Ev') {
    switch (type) {
      case 'AVERAGE':
        return (
          <>
            Ē<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>v</Text>
          </>
        );
      case 'MIN':
        return (
          <>
            E<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>v,min</Text>
          </>
        );
      case 'MAX':
        return (
          <>
            E<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>v,max</Text>
          </>
        );
      default:
        return (
          <>
            E<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>v</Text>
          </>
        );
    }
  }

  // Handle Esc (semi-cylindrical illuminance)
  if (func === 'Esc') {
    switch (type) {
      case 'AVERAGE':
        return (
          <>
            Ē<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>sc</Text>
          </>
        );
      case 'MIN':
        return (
          <>
            E<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>sc,min</Text>
          </>
        );
      case 'MAX':
        return (
          <>
            E<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>sc,max</Text>
          </>
        );
      default:
        return (
          <>
            E<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>sc</Text>
          </>
        );
    }
  }

  // Handle Ehs (horizontal illuminance on surface)
  if (func === 'Ehs') {
    switch (type) {
      case 'AVERAGE':
        return (
          <>
            Ē<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>hs</Text>
          </>
        );
      case 'MIN':
        return (
          <>
            E<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>hs,min</Text>
          </>
        );
      case 'MAX':
        return (
          <>
            E<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>hs,max</Text>
          </>
        );
      default:
        return (
          <>
            E<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>hs</Text>
          </>
        );
    }
  }

  // Handle E (illuminance)
  if (func === 'E') {
    switch (type) {
      case 'AVERAGE':
        return <>Ē</>;
      case 'MIN':
        return (
          <>
            E<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>min</Text>
          </>
        );
      case 'MAX':
        return (
          <>
            E<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>max</Text>
          </>
        );
      default:
        return <>E</>;
    }
  }

  // Handle L (luminance)
  if (func === 'L') {
    switch (type) {
      case 'AVERAGE':
        return <>L̄</>;
      case 'MIN':
        return (
          <>
            L<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>min</Text>
          </>
        );
      case 'MAX':
        return (
          <>
            L<Text style={{ verticalAlign: 'sub', fontSize: 8 }}>max</Text>
          </>
        );
      default:
        return <>L</>;
    }
  }

  // Fallback for any other function
  return func;
}
